import {
  Grid,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  Chip,
  Menu,
  MenuItem,
  Box,
  CircularProgress,
  TextField,
  InputAdornment,
  Tabs,
  Tab,
  Card,
  IconButton,
  useTheme,
  useMediaQuery,
  Pagination,
  Select,
  FormControl,
  Tooltip,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import PrintIcon from "@mui/icons-material/Print";
import AddIcon from "@mui/icons-material/Add";
import React, { useEffect, useRef, useState } from "react";
import { useAuth } from "../../../../context";
import useFetchObjects from "../../../../api/useFetchObjects";
import useUpdate from "../../../../api/useUpdate";
import Print from "./Print";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

const EchoCollectFees = () => {
  const { t } = useTranslation();
  const auth = useAuth();
  const token = auth?.user?.token;
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedEcho, setSelectedEcho] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [isPaidFilter, setIsPaidFilter] = useState("");
  const open = Boolean(anchorEl);
  const printRef = useRef();
  const [printData, setPrintData] = useState("");
  const navigate = useNavigate();

  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));

  // Pagination States
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalXrayPayment, setTotalEchoPayment] = useState(0);

  const { data, refetch, isLoading, isError } = useFetchObjects(
    ["EchoPaymentStatus", searchQuery, isPaidFilter, page, rowsPerPage],
    `patient-echo/?search=${searchQuery}&is_paid=${isPaidFilter}&page=${page}&page_size=${rowsPerPage}`,
    token
  );

  const { handleUpdate } = useUpdate("patient-echo", token);

  useEffect(() => {
    refetch();
  }, [searchQuery, isPaidFilter, refetch, page, rowsPerPage]);

  useEffect(() => {
    if (data && typeof data.count === "number") {
      setTotalEchoPayment(data.count);
    }
  }, [data]);

  const handlePageChange = (event, newPage) => {
    setPage(newPage);
  };

  const handleRowsPerPageChange = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(1);
  };

  const handleMenuClick = (event, echo) => {
    setAnchorEl(event.currentTarget);
    setSelectedEcho(echo);
  };

  const handleClose = () => {
    setAnchorEl(null);
    setSelectedEcho(null);
  };

  const handleMarkAsPaid = () => {
    const formData = new FormData();
    formData.append("is_paid", true);
    handleUpdate(selectedEcho.id, formData);
    handleClose();
    setIsPaidFilter(""); // Show All so the record stays visible as Paid
    refetch();
  };

  const handleSearchChange = (event) => {
    setSearchQuery(event.target.value);
  };

  const handleTabChange = (event, newValue) => {
    if (newValue === 0) {
      setIsPaidFilter("");
    } else if (newValue === 1) {
      setIsPaidFilter("false");
    } else if (newValue === 2) {
      setIsPaidFilter("true");
    }
  };

  const handlePrintClick = (echo) => {
    setPrintData(echo);
    setTimeout(() => {
      if (printRef.current) {
        const printWindow = window.open("", "_blank");
        printWindow.document.write(printRef.current.innerHTML);
        printWindow.document.close();
        printWindow.focus();
        printWindow.print();
        printWindow.close();
      }
    }, 500);
  };

  return (
    <Grid container justifyContent="center" mt={4} px={2}>
      <Grid item xs={12} md={12}>
        <Card
          variant="outlined"
          sx={{
            padding: "20px",
            boxShadow: "0px 4px 12px rgba(0,0,0,0.1)",
            borderRadius: "12px",
            mb: 4,
          }}
        >
          <Box sx={{ display: "flex", justifyContent: "space-between" }}>
            <Typography
              variant="h5"
              textAlign="center"
              fontWeight="bold"
              mb={2}
              color="primary"
            >
              {t("reception.echoPaymentStatus")}
            </Typography>

            <Button
              size="small"
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => navigate("/reception/collect-echo-fees/add")}
              sx={{
                textTransform: "none",
                fontWeight: "bold",
                paddingX: 3,
                paddingY: isSmallScreen ? 1 : 1.5,
                alignSelf: isSmallScreen ? "flex-end" : "center",
                borderRadius: "8px",
                boxShadow: 3,
                transition: "all 0.3s ease",
                backgroundColor: theme.palette.primary,
                "&:hover": {
                  backgroundColor: theme.palette.primary,
                  boxShadow: 6,
                  transform: "translateY(-2px)",
                },
                "&:active": {
                  boxShadow: 2,
                  transform: "translateY(0px)",
                },
                "& .MuiButton-startIcon": {
                  marginRight: 1,
                },
              }}
            >
              {t("reception.addNew")}
            </Button>
          </Box>

          {/* Search and Filter Area */}
          <Box display="flex" flexDirection="column" alignItems="center" mb={2}>
            <TextField
              size="small"
              variant="outlined"
              placeholder={t("reception.searchByPatientEchoType")}
              value={searchQuery}
              onChange={handleSearchChange}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon color="action" />
                  </InputAdornment>
                ),
              }}
              sx={{
                width: "100%",
                maxWidth: "400px",
                mb: 2,
                boxShadow: "0px 2px 8px rgba(0,0,0,0.1)",
                borderRadius: "8px",
              }}
            />

            <Tabs
              value={isPaidFilter === "" ? 0 : isPaidFilter === "false" ? 1 : 2}
              onChange={handleTabChange}
              aria-label="payment status tabs"
              centered
              sx={{
                maxWidth: "500px",
                width: "100%",
                "& .MuiTabs-flexContainer": {
                  justifyContent: "space-evenly",
                },
              }}
            >
              <Tab
                label={t("reception.all")}
                sx={{
                  fontWeight: "bold",
                  textTransform: "none",
                  fontSize: "1rem",
                  color: "text.primary",
                }}
              />
              <Tab
                label={t("reception.unpaid")}
                sx={{
                  fontWeight: "bold",
                  textTransform: "none",
                  fontSize: "1rem",
                  color: "text.primary",
                }}
              />
              <Tab
                label={t("reception.paid")}
                sx={{
                  fontWeight: "bold",
                  textTransform: "none",
                  fontSize: "1rem",
                  color: "text.primary",
                }}
              />
            </Tabs>
          </Box>
        </Card>

        {/* Table Section */}
        {isLoading ? (
          <Box display="flex" justifyContent="center" alignItems="center">
            <CircularProgress />
          </Box>
        ) : isError ? (
          <Box color="red" textAlign="center">
            {t("reception.errorF")}
          </Box>
        ) : (
          <TableContainer
            component={Paper}
            sx={{
              boxShadow: "0px 4px 12px rgba(0,0,0,0.1)",
              borderRadius: "12px",
            }}
          >
            <Table aria-label="echo payment status table">
              <TableHead>
                <TableRow>
                  <TableCell
                    align="center"
                    sx={{ fontWeight: "bold", backgroundColor: "grey.200" }}
                  >
                    {t("reception.date")}
                  </TableCell>
                  <TableCell
                    align="center"
                    sx={{ fontWeight: "bold", backgroundColor: "grey.200" }}
                  >
                    {t("reception.patientName")}
                  </TableCell>
                  <TableCell
                    align="center"
                    sx={{ fontWeight: "bold", backgroundColor: "grey.200" }}
                  >
                    {t("reception.echoType")}
                  </TableCell>
                  <TableCell
                    align="center"
                    sx={{ fontWeight: "bold", backgroundColor: "grey.200" }}
                  >
                    {t("reception.totalAmount")}
                  </TableCell>
                  <TableCell
                    align="center"
                    sx={{ fontWeight: "bold", backgroundColor: "grey.200" }}
                  >
                    {t("reception.status")}
                  </TableCell>
                  <TableCell
                    align="center"
                    sx={{ fontWeight: "bold", backgroundColor: "grey.200" }}
                  >
                    {t("reception.action")}
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody sx={{ bgcolor: "#ccfcfc" }}>
                {data?.results?.map((echo) => (
                  <TableRow
                    key={echo.id}
                    sx={{ "&:hover": { backgroundColor: "#f5f5f5" } }}
                  >
                    <TableCell align="center">{echo.date}</TableCell>
                    <TableCell align="center">{echo.patient_display}</TableCell>
                    <TableCell align="center">
                      {echo.echo_type_display}
                    </TableCell>
                    <TableCell align="center">{echo.total_amount}</TableCell>
                    <TableCell align="center">
                      <Chip
                        label={
                          echo.is_paid
                            ? t("reception.paid")
                            : t("reception.pending")
                        }
                        color={echo.is_paid ? "success" : "warning"}
                        sx={{ fontWeight: "bold" }}
                      />
                    </TableCell>
                    <TableCell align="center">
                      {!echo.is_paid && (
                        <>
                          <Button
                            aria-controls={
                              open ? "payment-action-menu" : undefined
                            }
                            aria-haspopup="true"
                            onClick={(event) => handleMenuClick(event, echo)}
                            sx={{ minWidth: "auto", color: "#1976d2" }}
                          >
                            <MoreHorizIcon />
                          </Button>
                          <Menu
                            id="payment-action-menu"
                            anchorEl={anchorEl}
                            open={open}
                            onClose={handleClose}
                            anchorOrigin={{
                              vertical: "top",
                              horizontal: "left",
                            }}
                            transformOrigin={{
                              vertical: "top",
                              horizontal: "left",
                            }}
                          >
                            <MenuItem onClick={handleMarkAsPaid}>
                              {t("reception.markPaid")}
                            </MenuItem>
                          </Menu>
                        </>
                      )}

                      <Tooltip title={t("reception.printReceipt", "Print receipt")}>
                        <IconButton
                          color="primary"
                          onClick={() => handlePrintClick(echo)}
                        >
                          <PrintIcon />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Grid>

      {/* Pagination Controls */}
      <Grid item xs={12}>
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          mt={4}
          mb={2}
        >
          {/* Rows Per Page Selector */}
          <Box display="flex" alignItems="center">
            <Typography variant="body2" mr={2}>
              {t("reception.rowsPerPage")}:
            </Typography>
            <FormControl variant="outlined" size="small">
              <Select
                value={rowsPerPage}
                onChange={handleRowsPerPageChange}
                sx={{
                  width: 80,
                  "& .MuiOutlinedInput-notchedOutline": {
                    border: "none",
                  },
                }}
              >
                {[10, 25, 50, 100, 500, 1000].map((rows) => (
                  <MenuItem key={rows} value={rows}>
                    {rows}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          {/* Pagination Component */}
          <Pagination
            count={Math.ceil(totalXrayPayment / rowsPerPage)}
            page={page}
            onChange={handlePageChange}
            variant="outlined"
            color="primary"
            showFirstButton
            showLastButton
          />
        </Box>
      </Grid>

      {printData && (
        <div ref={printRef} style={{ display: "none" }}>
          <Print echo={printData} />
        </div>
      )}
    </Grid>
  );
};

export default EchoCollectFees;
